/**
 * Hook for filtering rooms based on organization selection.
 * Supports both organization-aware and organization-agnostic modes.
 */

import { toHexFromId } from '#~/api/index.js';
import { useMemo } from 'react';
import { useDepot } from '#~/depot/hooks.js';
import type { RoomRecord } from '@studio/models/room';
import { shallow } from 'zustand/shallow';

export interface UseRoomFilteringOptions {
  /**
   * Optional organization ID to filter rooms by.
   * When provided, only rooms belonging to this organization are returned.
   * When undefined, all accessible rooms are returned.
   */
  organizationId?: Uint8Array | undefined;

  /**
   * Additional filter function to apply to rooms.
   * Useful for permission-based filtering (e.g., canUpdate).
   */
  additionalFilter?: ((room: RoomRecord) => boolean) | undefined;
}

export interface UseRoomFilteringResult {
  /**
   * Filtered list of rooms based on the provided options.
   */
  filteredRooms: RoomRecord[];

  /**
   * All available rooms (before filtering).
   */
  allRooms: RoomRecord[];
}

/**
 * Hook to filter rooms based on organization and other criteria.
 *
 * @param options - Filtering options
 * @returns Filtered rooms and all available rooms
 *
 * @example
 * ```tsx
 * // Filter by organization
 * const { filteredRooms } = useRoomFiltering({
 *   organizationId: selectedOrgId,
 *   additionalFilter: (room) => room.permissions.canUpdate
 * });
 *
 * // Get all rooms (no organization filter)
 * const { filteredRooms } = useRoomFiltering({
 *   additionalFilter: (room) => room.permissions.canUpdate
 * });
 * ```
 */
export function useRoomFiltering(options: UseRoomFilteringOptions = {}): UseRoomFilteringResult {
  const { organizationId, additionalFilter } = options;

  const [rooms] = useDepot((µ) => [µ.roomRecords], shallow);

  const allRooms = useMemo(() => Object.values(rooms), [rooms]);

  const filteredRooms = useMemo(() => {
    let result = allRooms;

    if (organizationId) {
      const orgIdHex = toHexFromId(organizationId);
      if (!orgIdHex) {
        return result;
      }

      result = result.filter((room) => {
        const roomOrgId = room.organizationId;
        // RoomRecord.organizationId is already a hex string, so direct comparison
        return roomOrgId === orgIdHex;
      });
    }

    // Apply additional filter if provided
    if (additionalFilter) {
      result = result.filter(additionalFilter);
    }

    return result;
  }, [allRooms, organizationId, additionalFilter]);

  return { filteredRooms, allRooms };
}
